import type { Agreements } from '../agreement';
import {
  SearchParameterAvailability,
  type SearchParameterSiteExclusions,
} from '../searchParameterAvailability';
import type { SearchParameters } from '../searchParameters';
import type { SearchPreferences } from '../searchPreferences';
import type { SortProfile } from '../sortSelection';
import { ResolvedSearch } from './resolvedSearch';
import { Search } from './search';

/**
 * What every search needs and none can derive, as plain values: the visitor's
 * stored preferences, their sort profile, and the site's parameter exclusions.
 *
 * Plain values rather than domain objects, so a caller hands over what it read
 * — the cookie, the profile, the site config — and never has to assemble the
 * availability or the resolver itself.
 */
export interface SearchFactoryInputs {
  preferences: SearchPreferences;
  sortProfile: SortProfile;
  siteExclusions: SearchParameterSiteExclusions;
}

/**
 * Makes a `Search`, at one of the two ways a visitor can arrive at one.
 *
 * Built once from what every search needs and none can derive — the visitor's
 * preferences, their sort profile, the site's exclusions — and kept by every
 * search it makes, so a transition reads the same context as the entry did.
 *
 * Takes parameters rather than a url: reading the url, and which of the route's
 * segments state what, is the route's job.
 */
export class SearchFactory {
  readonly #resolver: ResolvedSearch;

  readonly #availability: SearchParameterAvailability;

  constructor({
    preferences,
    sortProfile,
    siteExclusions,
  }: SearchFactoryInputs) {
    this.#availability = new SearchParameterAvailability(siteExclusions);
    this.#resolver = new ResolvedSearch({
      preferences,
      sortProfile,
      availability: this.#availability,
    });
  }

  /**
   * A cold entry: the visitor arrived with a url and nothing before it.
   *
   * One of the two moments preferences apply — the other is a transition that
   * changes scope. Fills what the url leaves unsaid from the visitor's
   * preferences, derives what the search implies, and clears what it cannot
   * carry, in the order `ResolvedSearch#onEntry` sets out.
   */
  enter(stated: SearchParameters, agreements: Agreements): Search {
    return new Search(
      this.#resolver,
      this.#availability,
      this.#resolver.onEntry(stated, agreements)
    );
  }

  /**
   * A search this app already resolved, back again — the url a transition
   * produced, reached by navigating to it.
   *
   * Never applies preferences. The url already carries every choice the
   * visitor made, including the ones they cleared, and filling it again would
   * put back a preference they had just taken off. Only clears what the search
   * cannot carry, which leaves an already-resolved search as it was.
   */
  restore(searchParameters: SearchParameters): Search {
    return new Search(this.#resolver, this.#availability, searchParameters);
  }
}
